import { useEffect, useState, useCallback, useRef } from "react";
import { extractApiPath, getFromDBandFile, setToDB } from "src/core/api/cache/idbUtility";
import { getFromMemory } from "src/core/api/cache/memoryCache";

export function useCache<T>(fullKey: string): [T | null, React.Dispatch<React.SetStateAction<T | null>>, boolean] {
  const key = extractApiPath(fullKey);
  const [state, setState] = useState<T | null>(null);
  const [isCacheLoaded, setIsCacheLoaded] = useState(false);
  const valueRevision = useRef(0);
  useEffect(() => {
    let cancelled = false;
    let timeoutId: number | null = null;
    const hydrationRevision = valueRevision.current;
    const memoryValue = getFromMemory<T>(key);
    if (memoryValue !== undefined && memoryValue !== null) {
      timeoutId = window.setTimeout(() => {
        if (!cancelled) {
          if (valueRevision.current === hydrationRevision) setState(memoryValue);
          setIsCacheLoaded(true);
        }
      }, CACHE_DELAY_MS);
      return () => {
        cancelled = true;
        if (timeoutId !== null) window.clearTimeout(timeoutId);
      };
    }
    const fetchFromCache = async () => {
      try {
        const cachedValue = await getFromDBandFile<T>(key);
        if (cancelled) return;
        if (valueRevision.current === hydrationRevision && cachedValue !== undefined && cachedValue !== null) {
          setState(cachedValue);
        }
      } catch (error) {
        console.error("Failed to load cache for key:", key, error);
      } finally {
        if (!cancelled) {
          setIsCacheLoaded(true);
        }
      }
    };
    setIsCacheLoaded(false);
    fetchFromCache();
    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [key]);
  const setCachedValue: React.Dispatch<React.SetStateAction<T | null>> = useCallback((valueOrUpdater) => {
    // An accepted value wins over cache hydration that started before it.
    valueRevision.current += 1;
    setState((prev) => {
      const next = typeof valueOrUpdater === "function" ? (valueOrUpdater as (p: T | null) => T | null)(prev) : valueOrUpdater;
      setToDB(key, next as T);
      return next;
    });
  }, [key]);
  return [state, setCachedValue, isCacheLoaded];
}

const CACHE_DELAY_MS = 30;

export default useCache;
